/*
  TypeTopia — my personalisation (v2.2)
  assets/tt-my-personalisation.js

  v2.2 (2026-10-08, Ally: "make a pfp option & name"): the profile, { name,
  url } (worker v3.2): profile, setProfile({ name, url }). So the header can
  say "Ally" with their face from the first frame (no "Account" swapping to
  a name a moment later), a copy of the profile sits next to the token in
  localStorage "tt.me" as { n, u } (their first name and the picture's
  address), dropped with the token on signing out. The one addition to
  "the browser keeps only the token".

  v2.1 (2026-10-08, Ally: "need to be able to reorder"): orders(), the
  signed-in customer's past orders from the worker's GET /orders (worker
  v3.1), for the my personalisation page's "order again". Nothing about
  them is kept: each call asks again.

  v2 (2026-10-08, Ally: "we decided to not use shopify accounts"): TypeTopia's
  own sign-in. The customer types their email, gets a 6-digit code from
  TypeTopia, types it in (assets/tt-signin.js draws that), and is signed in
  on this device for 90 days. Everything lives on the typetopia-personalisation
  worker, called straight from here (CORS); nothing goes through Shopify's
  customer accounts any more.

  The browser keeps ONE thing: the sign-in token (a random key, nothing else:
  no email, no faces), in localStorage under "tt.signin" as { t, x } with its
  expiry, every read and write in try/catch (private windows, blocked storage:
  then it lives in memory for this page only). snippets/tt-my-personalisation
  sets html.tt-in from it before the first paint, so signed-in-only parts
  (.tt-only-in) and signed-out-only parts (.tt-only-out) are right at once;
  the saved items arrive a moment later (/me) and personalisers draw them in
  space they've kept for them.

    TTMyPersonalisation.signedIn            true / false (live)
    TTMyPersonalisation.email               the signed-in email, once /me has answered ("" before)
    TTMyPersonalisation.ready               Promise: the first /me has answered (or signed out)
    TTMyPersonalisation.loaded              true once the saved items are in
    TTMyPersonalisation.list(type?)          newest first; type "face" | "logo" | "text"
    TTMyPersonalisation.add(item)            Promise<item | null>
    TTMyPersonalisation.remove(id)           Promise<boolean>
    TTMyPersonalisation.clear()              Promise<boolean>
    TTMyPersonalisation.refresh()            Promise: re-reads the account
    TTMyPersonalisation.signIn(email)        Promise<{ ok, error }>: sends the code
    TTMyPersonalisation.verify(code)         Promise<{ ok, error, triesLeft }>: signs in, then refreshes
    TTMyPersonalisation.signOut()            Promise<true>
    TTMyPersonalisation.profile             { name, url } or null (v2.2): from the device's copy at once, then /me
    TTMyPersonalisation.setProfile(p)       Promise<{ ok, error }> (v2.2): { name, url }, "" clears either
    TTMyPersonalisation.orders()             Promise<{ ok, orders | error }> (v2.1): past orders,
                                             [{ name, date, status, items: [{ title, variant, qty,
                                             properties, image, variantId, available, handle }] }]
    TTMyPersonalisation.openSignIn(opts?)    opens the sign-in sheet (assets/tt-signin.js)
    TTMyPersonalisation.signInUrl()          the my personalisation page (sign-in is there too)
    document "tt:mypersonalisation" event    after any change, and on signing in or out
                                             (detail: { items, signedIn, email, profile })

  Items: { id, type: "face", url, originalUrl } · { id, type: "logo", url }
         · { id, type: "text", text }
*/
(function () {
  "use strict";
  if (window.TTMyPersonalisation) return;

  var cfg = window.TTAccountConfig || {};
  var API = String(cfg.api || "https://typetopia-personalisation.sweet-tooth-c156.workers.dev").replace(/\/+$/, "");
  var PAGE = cfg.page || "/pages/my-personalisation";
  var KEY = "tt.signin";

  function readToken() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || "null");
      if (v && typeof v.t === "string" && v.x > Date.now()) return v.t;
      if (v) localStorage.removeItem(KEY);
    } catch (e) {}
    return "";
  }
  function keepToken(t, x) { try { localStorage.setItem(KEY, JSON.stringify({ t: t, x: x })); } catch (e) {} }
  function dropToken() { try { localStorage.removeItem(KEY); localStorage.removeItem(ME); } catch (e) {} }
  /* (v2.2) the device's copy of the profile, for the header's first frame */
  var ME = "tt.me";
  function readMe() {
    try { var v = JSON.parse(localStorage.getItem(ME) || "null"); if (v && (v.n || v.u)) return { name: String(v.n || ""), url: String(v.u || "") }; } catch (e) {}
    return null;
  }
  function keepMe(p) {
    try { if (p && (p.name || p.url)) localStorage.setItem(ME, JSON.stringify({ n: p.name || "", u: p.url || "" })); else localStorage.removeItem(ME); } catch (e) {}
  }

  var token = readToken();
  var email = "", pending = "", items = [], loaded = false;
  var profile = token ? readMe() : null;
  if (!token) keepMe(null);

  function valid(i) { return i && i.id && (i.type === "face" || i.type === "logo" || i.type === "text"); }
  function mark() { document.documentElement.classList.toggle("tt-in", !!token); }
  mark();

  function announce() {
    document.dispatchEvent(new CustomEvent("tt:mypersonalisation", {
      detail: { items: items.slice(), signedIn: !!token, email: email, profile: profile }
    }));
  }

  // signed out here (the token ran out, or was signed out on the server): forget it quietly
  function lost() {
    if (!token) return;
    token = ""; email = ""; items = []; loaded = true; profile = null;
    dropToken(); mark(); announce();
  }

  function req(method, path, body, auth) {
    var headers = { Accept: "application/json" };
    if (auth) headers.Authorization = "Bearer " + token;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    return fetch(API + path, {
      method: method, headers: headers, credentials: "omit", mode: "cors", cache: "no-store",
      body: body !== undefined ? JSON.stringify(body) : undefined
    })
      .then(function (r) { return r.json().catch(function () { return { ok: false, error: "server" }; }); })
      .catch(function () { return { ok: false, error: "network" }; });
  }

  // signed-in calls: a reply with items puts them in and tells everyone
  function call(method, path, body) {
    if (!token) return Promise.resolve(null);
    var sent = token;
    return req(method, path, body, true).then(function (j) {
      if (sent !== token) return null;                  // signed out or in again meanwhile
      if (!j || !j.ok) {
        if (j && j.error === "signed_out") lost();
        return null;
      }
      if (typeof j.email === "string") email = j.email;
      var said = false;
      if ("profile" in j) { profile = j.profile && (j.profile.name || j.profile.url) ? { name: String(j.profile.name || ""), url: String(j.profile.url || "") } : null; keepMe(profile); }
      if (Array.isArray(j.items)) { items = j.items.filter(valid); loaded = true; announce(); said = true; }
      if (!said && "profile" in j) announce();
      return j;
    });
  }

  var readyDone;
  var api = {
    ready: new Promise(function (res) { readyDone = res; }),
    list: function (type) {
      return items.filter(function (i) { return !type || i.type === type; });
    },
    add: function (item) {
      return call("POST", "/items/add", { item: item }).then(function (j) { return j && j.item ? j.item : null; });
    },
    remove: function (id) {
      return call("POST", "/items/remove", { id: id }).then(function (j) { return !!j; });
    },
    clear: function () {
      return call("POST", "/items/clear", {}).then(function (j) { return !!j; });
    },
    refresh: function () {
      if (!token) { loaded = true; return Promise.resolve([]); }
      return call("GET", "/me").then(function () {
        if (!loaded && token) { loaded = true; announce(); }   // unreachable: show what we have (nothing), not a gap for ever
        return items.slice();
      });
    },
    signIn: function (address) {
      var e = String(address || "").trim().toLowerCase();
      pending = e;
      return req("POST", "/auth/start", { email: e }, false).then(function (j) {
        return { ok: !!(j && j.ok), error: j && !j.ok ? j.error || "server" : "" };
      });
    },
    verify: function (code, address) {
      var e = String(address || pending || "").trim().toLowerCase();
      var c = String(code || "").replace(/\D/g, "");
      return req("POST", "/auth/verify", { email: e, code: c }, false).then(function (j) {
        if (!j || !j.ok || !j.token) {
          return { ok: false, error: (j && j.error) || "server", triesLeft: j && j.triesLeft };
        }
        token = j.token; email = j.email || e; pending = "";
        keepToken(j.token, j.expires || Date.now() + 90 * 86400000);
        mark();
        announce();                                       // signed in: show signed-in parts now
        return api.refresh().then(function () { return { ok: true, error: "" }; });
      });
    },
    signOut: function () {
      var t = token;
      token = ""; email = ""; items = []; loaded = true; profile = null;
      dropToken(); mark(); announce();
      if (!t) return Promise.resolve(true);
      return fetch(API + "/auth/signout", {
        method: "POST", headers: { Authorization: "Bearer " + t }, credentials: "omit", mode: "cors", keepalive: true
      }).then(function () { return true; }, function () { return true; });
    },
    setProfile: function (p) {
      p = p || {};
      if (!token) return Promise.resolve({ ok: false, error: "signed_out" });
      var sent = token;
      return req("POST", "/profile", { name: String(p.name || ""), url: String(p.url || "") }, true).then(function (j) {
        if (sent !== token) return { ok: false, error: "signed_out" };
        if (j && j.error === "signed_out") lost();
        if (!j || !j.ok) return { ok: false, error: (j && j.error) || "server" };
        profile = j.profile && (j.profile.name || j.profile.url) ? j.profile : null;
        keepMe(profile); announce();
        return { ok: true, error: "" };
      });
    },
    orders: function () {
      if (!token) return Promise.resolve({ ok: false, error: "signed_out" });
      var sent = token;
      return req("GET", "/orders", undefined, true).then(function (j) {
        if (sent !== token) return { ok: false, error: "signed_out" };
        if (j && j.error === "signed_out") lost();
        if (!j || !j.ok || !Array.isArray(j.orders)) return { ok: false, error: (j && j.error) || "server" };
        return j;
      });
    },
    openSignIn: function (opts) {
      if (window.TTSignIn) { window.TTSignIn.open(opts || {}); return true; }
      location.href = PAGE;
      return false;
    },
    signInUrl: function () { return PAGE; },
    accountUrl: PAGE
  };
  Object.defineProperty(api, "signedIn", { get: function () { return !!token; }, enumerable: true });
  Object.defineProperty(api, "email", { get: function () { return email; }, enumerable: true });
  Object.defineProperty(api, "profile", { get: function () { return profile; }, enumerable: true });
  Object.defineProperty(api, "loaded", { get: function () { return loaded; }, enumerable: true });
  window.TTMyPersonalisation = api;

  // another tab signed in or out: follow it
  window.addEventListener("storage", function (e) {
    if (e.key !== KEY && e.key !== ME && e.key !== null) return;
    var t = readToken();
    if (t === token) { if (e.key === ME) { profile = t ? readMe() : null; announce(); } return; }
    token = t; email = ""; items = []; loaded = !t; profile = t ? readMe() : null;
    mark(); announce();
    if (t) api.refresh();
  });

  // first look: who's signed in, and their saved items
  api.refresh().then(function () { readyDone(); }, function () { readyDone(); });
})();
